import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, Check, Clock, Ban, Minus, Loader2, ExternalLink, Sparkles, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageLoading } from "@/components/shared";
import api, { formatApiErrorDetail } from "@/lib/api";

const prettify = (s) => (s || "").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
const STEP_ICON = { COMPLETED: Check, IN_PROGRESS: Loader2, WAITING: Clock, BLOCKED: Ban, NOT_REQUIRED: Minus, NOT_STARTED: Minus };
const STEP_TONE = {
  COMPLETED: "bg-emerald-500 text-white", IN_PROGRESS: "bg-cyan text-white", WAITING: "bg-amber-400 text-white",
  BLOCKED: "bg-red-500 text-white", NOT_REQUIRED: "bg-slate-200 text-slate-500", NOT_STARTED: "bg-slate-200 text-slate-400",
};
const VERDICT_TONE = { MOEGLICH: "border-emerald-200 bg-emerald-50 text-emerald-700", PRUEFUNG: "border-amber-200 bg-amber-50 text-amber-700", WEITERE_ANGABEN: "border-slate-200 bg-slate-50 text-slate-500" };

export default function ImmigrationCaseDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [rules, setRules] = useState({});
  const [assess, setAssess] = useState(null);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/employer/immigration/cases/${id}`).then((r) => { setC(r.data); setPick(r.data.pathway); }).catch(() => { toast.error("Error"); nav("/app/employer/immigration"); }), [id]);
  useEffect(() => { load(); api.get("/employer/immigration/rules").then((r) => setRules(r.data.rules || {})).catch(() => {}); }, [id]);

  const runAssess = () => c && api.get(`/employer/immigration/assess?worker_id=${c.worker_id}`).then((r) => setAssess(r.data)).catch(() => {});
  useEffect(() => { if (c && !assess) runAssess(); /* eslint-disable-next-line */ }, [c]);

  const selectPathway = async () => {
    if (!pick) return;
    setBusy(true);
    try { const { data } = await api.post(`/employer/immigration/cases/${id}/pathway`, { pathway: pick }); setC(data); toast.success(t("im.pathwaySaved")); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const cycleStep = async (step) => {
    const order = ["NOT_STARTED", "IN_PROGRESS", "WAITING", "COMPLETED", "BLOCKED", "NOT_REQUIRED"];
    const next = order[(order.indexOf(step.status) + 1) % order.length];
    try { const { data } = await api.post(`/employer/immigration/cases/${id}/steps/${step.key}/status`, { status: next }); setC(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const bookService = async (service_type) => {
    try { const { data } = await api.post(`/employer/immigration/cases/${id}/service-checkout`, { service_type, origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!c) return <PageLoading />;
  const baUrl = rules.BA_VORABZUSTIMMUNG_URL?.value;
  const vmUrl = rules.VISAMETRIC_URL?.value;

  return (
    <div data-testid="immigration-case-detail" className="max-w-4xl">
      <button onClick={() => nav("/app/employer/immigration")} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-navy"><ArrowLeft className="h-4 w-4" /> {t("im.nav")}</button>
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <h1 className="font-heading text-2xl font-extrabold text-navy sm:text-3xl">{c.worker_name}</h1>
        <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">{c.nationality || "—"}</span>
        <span className="rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-xs font-semibold text-cyan-700">{c.pathway_label}</span>
        <span className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600">{prettify(c.status)}</span>
      </div>

      {/* Passenden Weg prüfen */}
      {assess && (
        <div className="card-soft mb-6 p-6" data-testid="imm-assess">
          <div className="mb-3 flex items-center gap-2 font-heading font-bold text-navy"><Sparkles className="h-4 w-4 text-cyan-dark" /> {t("im.checkPathway")}</div>
          <div className="flex flex-wrap gap-2">
            {assess.results.map((r) => (
              <span key={r.pathway} className={`rounded-full border px-3 py-1 text-xs font-semibold ${VERDICT_TONE[r.verdict] || "border-slate-200 text-slate-500"}`} title={r.reasons.join("; ")}>
                {r.label}: {t(`im.v.${r.verdict}`)}
              </span>
            ))}
          </div>
          {assess.missing?.length > 0 && <p className="mt-3 text-xs text-slate-500">{t("im.stillNeeded")}: {assess.missing.join(", ")}</p>}
        </div>
      )}

      {/* Pathway select */}
      <div className="card-soft mb-6 flex flex-wrap items-end gap-3 p-6">
        <div className="flex-1">
          <div className="mb-2 text-sm font-semibold text-navy">{t("im.selectPathway")}</div>
          <Select value={pick} onValueChange={setPick}>
            <SelectTrigger data-testid="imm-pathway-select"><SelectValue /></SelectTrigger>
            <SelectContent>{(assess?.results || []).length ? null : null}
              {["WESTERN_BALKANS", "SKILLED_WORKER", "EU_BLUE_CARD", "RECOGNITION_16D", "RECOGNITION_PARTNERSHIP", "FAST_TRACK_81A", "OTHER", "UNDECIDED"].map((p) => (
                <SelectItem key={p} value={p}>{t(`im.p.${p}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={selectPathway} disabled={busy || pick === c.pathway} className="rounded-full bg-navy text-white hover:bg-navy-muted" data-testid="imm-confirm-pathway">{t("im.confirmPathway")}</Button>
      </div>

      {/* Timeline */}
      {c.steps?.length > 0 && (
        <div className="card-soft mb-6 p-6" data-testid="imm-timeline">
          <div className="mb-4 font-heading font-bold text-navy">{t("im.timeline")}</div>
          <div className="space-y-1">
            {c.steps.map((s, i) => {
              const Icon = STEP_ICON[s.status] || Minus;
              return (
                <div key={s.key} className="flex items-start gap-3">
                  <div className="flex flex-col items-center">
                    <button onClick={() => cycleStep(s)} data-testid={`imm-step-${s.key}`} className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors ${STEP_TONE[s.status]}`}>
                      <Icon className={`h-4 w-4 ${s.status === "IN_PROGRESS" ? "animate-spin" : ""}`} />
                    </button>
                    {i < c.steps.length - 1 && <div className="my-1 h-6 w-px bg-slate-200" />}
                  </div>
                  <div className="pt-1">
                    <div className="text-sm font-semibold text-navy">{s.label}</div>
                    <div className="text-xs text-slate-400">{t(`im.ss.${s.status}`)}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="mt-4 text-xs text-slate-400">{t("im.stepHint")}</p>
        </div>
      )}

      {/* Official links */}
      {(baUrl || vmUrl) && (
        <div className="mb-6 flex flex-wrap gap-2">
          {baUrl && c.pathway === "WESTERN_BALKANS" && <a href={baUrl} target="_blank" rel="noreferrer" data-testid="imm-ba-link" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-navy hover:border-cyan-200"><ExternalLink className="h-3.5 w-3.5" /> Bundesagentur für Arbeit</a>}
          {vmUrl && <a href={vmUrl} target="_blank" rel="noreferrer" data-testid="imm-vm-link" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-navy hover:border-cyan-200"><ExternalLink className="h-3.5 w-3.5" /> VisaMetric</a>}
        </div>
      )}

      {/* Services */}
      <div className="card-soft mb-6 p-6" data-testid="imm-services">
        <div className="mb-4 font-heading font-bold text-navy">{t("im.bookedServices")}</div>
        <div className="grid gap-3 sm:grid-cols-2">
          {c.services_catalog.map((svc) => {
            const booked = (c.service_orders || []).find((o) => o.service_type === svc.key);
            return (
              <div key={svc.key} className="rounded-xl border border-slate-200 p-4" data-testid={`imm-service-${svc.key}`}>
                <div className="font-heading text-sm font-bold text-navy">{svc.label}</div>
                <p className="mt-1 text-xs text-slate-500">{svc.desc}</p>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-sm font-bold text-navy">{svc.price.toFixed(2)} € <span className="text-xs font-normal text-slate-400">{t("im.plusVat")}</span></span>
                  {booked
                    ? <span className="rounded-full bg-emerald-50 px-2 py-1 text-[11px] font-bold uppercase text-emerald-600">{prettify(booked.payment_status)} · {prettify(booked.service_status)}</span>
                    : <Button size="sm" onClick={() => bookService(svc.key)} className="rounded-full bg-cyan text-white hover:bg-cyan-dark" data-testid={`imm-book-${svc.key}`}>{t("im.bookSupport")}</Button>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-500" data-testid="imm-disclaimer">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" /> {c.disclaimer}
      </div>
    </div>
  );
}
